// client/src/utils/hasChaptersCanonical.ts
// Canonical hasChapters computation to ensure consistency across the application

import { pickChaptersArray, pickChaptersCount } from './cacheBootstrap';

/**
 * Canonical function to determine if a project has chapters
 * Single source of truth for chapter existence checking
 */
export function computeHasChapters(source: any): boolean {
  if (!source) return false;

  // Try multiple approaches to determine chapter existence

  // 1. Direct chapter count from derived state (highest priority)
  if (typeof source.chaptersCount === 'number') {
    return source.chaptersCount > 0;
  }

  // 2. Try to pick chapters array using tolerant picker
  const chaptersArray = pickChaptersArray(source);
  if (chaptersArray && Array.isArray(chaptersArray)) {
    return chaptersArray.length > 0;
  }

  // 3. Try to pick chapters count using tolerant picker
  const chaptersCount = pickChaptersCount(source);
  if (typeof chaptersCount === 'number') {
    return chaptersCount > 0;
  }

  // 4. Check various chapter count properties
  if (typeof source.chapterCount === 'number') {
    return source.chapterCount > 0;
  }

  // 5. Check explicit hasChapters flag (lowest priority due to potential staleness)
  if (typeof source.hasChapters === 'boolean') {
    return source.hasChapters;
  }

  // Default: no chapters
  return false;
}

/**
 * Compute canonical chapters count for consistency
 */
export function computeChaptersCount(source: any): number {
  if (!source) return 0;

  // 1. Direct chapter count from derived state (highest priority)
  if (typeof source.chaptersCount === 'number') {
    return source.chaptersCount;
  }

  // 2. Try to pick chapters array using tolerant picker
  const chaptersArray = pickChaptersArray(source);
  if (chaptersArray && Array.isArray(chaptersArray)) {
    return chaptersArray.length;
  }

  // 3. Try to pick chapters count using tolerant picker
  const chaptersCount = pickChaptersCount(source);
  if (typeof chaptersCount === 'number') {
    return chaptersCount;
  }

  // 4. Check various chapter count properties
  if (typeof source.chapterCount === 'number') {
    return source.chapterCount;
  }

  // Default: no chapters
  return 0;
}

/**
 * Helper to create consistent chapter metadata for caching
 */
export function createChapterMetadata(chapters: any[], projectData?: any): {
  hasChapters: boolean;
  chaptersCount: number;
  chapterCount: number; // Legacy compatibility
  chaptersGenerated: boolean;
  chaptersOutline: number;
} {
  const count = chapters?.length || 0;

  return {
    hasChapters: count > 0,
    chaptersCount: count,
    chapterCount: count, // Legacy compatibility
    chaptersGenerated: count > 0,
    chaptersOutline: count > 0 ? 100 : 0, // 100% if chapters exist, 0% otherwise
  };
}

/**
 * Validate chapter existence across multiple sources for debugging
 */
export function validateChapterConsistency(sources: Record<string, any>): {
  isConsistent: boolean;
  inconsistencies: string[];
  canonicalHasChapters: boolean;
  canonicalCount: number;
} {
  const results = Object.entries(sources).map(([name, source]) => ({
    name,
    hasChapters: computeHasChapters(source),
    count: computeChaptersCount(source)
  }));

  // Find the highest count (most authoritative)
  const maxCount = Math.max(...results.map(r => r.count));
  const canonicalHasChapters = maxCount > 0;

  // Check for inconsistencies
  const inconsistencies: string[] = [];
  results.forEach(result => {
    if (result.hasChapters !== canonicalHasChapters) {
      inconsistencies.push(`${result.name}: hasChapters=${result.hasChapters} but canonical=${canonicalHasChapters}`);
    }
    if (result.count !== maxCount && result.count > 0) {
      inconsistencies.push(`${result.name}: count=${result.count} but max=${maxCount}`);
    }
  });

  return {
    isConsistent: inconsistencies.length === 0,
    inconsistencies,
    canonicalHasChapters,
    canonicalCount: maxCount
  };
}